import { type Emitter } from "mitt";

import type { MittEvents } from "./appTypes";
import type { ActionLineForName, ChatMessage, ChatMessageLine } from "./types/chat";

export enum ChatAction {
    CUSTOMIZABLE_TEXT = "customizable_text",
    FOLLOW_UP = "followup",
    FILE_UPLOAD = "file_upload",
    RATING = "rating",
    ROLE_PLAY_ADJUST = "role_play_adjust",
    INTERNAL_THINKING = "internal_thinking",
    ACTION_ITEMS = "action_items",
    VALUES_INSIGHT = "values_insight",
    TIP = "tip",
    LLM_EXPLANATION = "llm_explanation",
    PROFILE_QUESTION = "profile_question",
    INFERRED_PROFILE_ANSWER = "inferred_profile_answer",
    ROLE_PLAY_MONITOR = "role_play_monitor",
    TOOL_CALL = "tool_call",
    EMAIL_CHAT_SUMMARY = "email_chat_summary",
    BACK_TO_OVERVIEW = "back_to_overview",
    LOADING = "loading",
    TAGGED_PERSON = "tagged_person",
    DOCUMENT_CREATOR = "document_creator",
    RECOMMEND_LEARNING_CONTENT = "recommend_learning_content",
    ROLE_PLAY_SETUP = "role_play_setup",
    ROLE_PLAY_SUMMARY = "role_play_summary",
    VOICE_NUDGE = "voice_nudge",
    WORKIQ_COLLABORATION_CONTEXT = "get_collaboration_context",
    WORKIQ_COLLABORATION_SOURCES = "collaboration_sources",
    WORKIQ_MY_WORK_CONTEXT = "get_my_work_context",
    PM_PICKER = "pm_picker",
    PROACTIVE_COACHING_OFFER = "proactive_coaching_offer",
    PUSH_REVIEW_TO_WORKDAY = "push_review_to_workday",
    SET_UP_DEVELOPMENT_GOAL = "set_up_development_goal",
    DEVELOPMENT_GOAL_LOCKED_IN = "development_goal_locked_in",
    PUSH_MANAGER_REVIEW_TO_WORKDAY = "push_manager_review_to_workday",
    DRAFT_MANAGER_REVIEW_CARD = "draft_manager_review_card",
    SUBMIT_MANAGER_REVIEW_DRAFT = "submit_manager_review_draft",
    DRAFT_SELF_REVIEW_CARD = "draft_self_review_card",
    SUBMIT_SELF_REVIEW_DRAFT = "submit_self_review_draft",
    DRAFT_GOAL_PUSH_CARD = "draft_goal_push_card",
    SUBMIT_GOAL_PUSH_DRAFT = "submit_goal_push_draft",
    EMAIL_DRAFT = "email_draft",
}

export type ChatActionType = `${ChatAction}`;

// I did this so I don't go and change ACTION to ChatAction in every reference
export const ACTION = ChatAction;

export const SIDEBAR_WIDGETS = [
    ACTION.CUSTOMIZABLE_TEXT,
    ACTION.FOLLOW_UP,
    ACTION.INTERNAL_THINKING,
    ACTION.ACTION_ITEMS,
    ACTION.VALUES_INSIGHT,
    ACTION.INFERRED_PROFILE_ANSWER,
    ACTION.ROLE_PLAY_MONITOR,
];

export const CHAT_WIDGETS = [
    ACTION.ROLE_PLAY_ADJUST,
    ACTION.CUSTOMIZABLE_TEXT,
    ACTION.FOLLOW_UP,
    ACTION.INTERNAL_THINKING,
    ACTION.ACTION_ITEMS,
    ACTION.VALUES_INSIGHT,
    ACTION.DOCUMENT_CREATOR,
];

/*
 * For tallying up the notes count, all applicable action instances count
 * as 1, except for action_items where we count each action item.
 */
export function getNotesCount(messages: ChatMessage[]) {
    let count = 0;

    for (const message of messages) {
        for (const line of message.lines) {
            if (!lineIsAction(line) || !lineIsWidget(line) || line.action_state?.dismissed) {
                continue;
            }

            switch (line.action_name) {
                case ACTION.ACTION_ITEMS:
                    count += line.action_params?.action_items?.length ?? 0;
                    break;

                default:
                    count++;
            }
        }
    }

    return count;
}

// these are actions that get "buffered", meaning the action will come back on the user message
// but it gets move to the next assistant message and placed below it
export const LOOKBACK_ACTIONS = [ACTION.ACTION_ITEMS];

export const lineIsAction = (line: ChatMessageLine) => {
    return line.type === "action";
};

/** Type guard that narrows a ChatMessageLine to the specific action type(s) matching the given action names */
export const isActionOfType = <T extends ChatAction>(line: ChatMessageLine, actionNames: readonly T[]): line is ActionLineForName<T> => {
    return line.type === "action" && (actionNames as readonly ChatAction[]).includes(line.action_name);
};

export const lineIsWidget = (line: ChatMessageLine, chatAction: boolean = false) => {
    const widgets = chatAction ? CHAT_WIDGETS : SIDEBAR_WIDGETS;
    return lineIsAction(line) && widgets.includes(line.action_name);
};

export const lineHasWidget = (line: ChatMessageLine, widgetName: ChatAction, chatAction: boolean = false) => {
    return lineIsAction(line) && lineIsWidget(line, chatAction) && line.action_name === widgetName;
};

export type PluckedAction<T extends ChatAction> = { line: ActionLineForName<T>; lineIdx: number; message: ChatMessage };

export const pluckMultipleActions = <T extends ChatAction>(messages: ChatMessage[] = [], actionNames: T | T[] = [] as T[]) => {
    const names = Array.isArray(actionNames) ? actionNames : [actionNames];
    const actions: PluckedAction<T>[] = [];

    for (const message of messages) {
        for (const line of message.lines) {
            if (isActionOfType(line, names)) {
                actions.push({
                    line,
                    lineIdx: message.lines.indexOf(line),
                    message,
                });
            }
        }
    }

    return actions;
};

export const pluckAction = <T extends ChatAction>(chatMessages: ChatMessage[], actionName: T, chatAction: boolean = false): PluckedAction<T> | null => {
    for (const message of chatMessages) {
        for (const line of message.lines) {
            if (isActionOfType(line, [actionName]) && lineIsWidget(line, chatAction)) {
                return {
                    line,
                    lineIdx: message.lines.indexOf(line),
                    message,
                };
            }
        }
    }

    return null;
};

export const actionExists = (chatMessages: ChatMessage[], actionName: ChatAction) => {
    return chatMessages.some((message) => message.lines.some((l) => lineHasWidget(l, actionName)));
};

export function someActionExists(chatMessages: ChatMessage[], actionNames: ChatAction[] = []) {
    return chatMessages.some((message) => message.lines.some((l) => lineIsAction(l) && actionNames.includes(l.action_name)));
}

export const messageHasActions = (message: ChatMessage, actionName: ChatAction[] = []) =>
    message.lines.some((l) => lineIsAction(l) && (actionName.length === 0 || actionName.includes(l.action_name)));

const isRolePlayMonitor = (line: ChatMessageLine) => lineIsAction(line) && line.action_name === ACTION.ROLE_PLAY_MONITOR;
export const isRolePlaySummary = (line: ChatMessageLine) => lineIsAction(line) && line.action_name === ACTION.ROLE_PLAY_SUMMARY && !!line.action_params?.role_play_hashid;

export const isRolePlayMessage = (message: ChatMessage) => {
    return message.lines.some((l) => isRolePlayMonitor(l));
};

export const getRolePlayMonitorParams = (message: ChatMessage) => {
    const action = message.lines.find((l) => isRolePlayMonitor(l));
    return action?.type === "action" ? action.action_params : null;
};

export const dismissAction = ({
    emitter,
    eventName,
    line,
    messageId,
    lineIdx,
}: {
    emitter: Emitter<MittEvents>;
    eventName: keyof MittEvents;
    line: ChatMessageLine;
    messageId: string;
    lineIdx: number;
}) => {
    if (lineIsAction(line)) {
        const action_data = {
            message_id: messageId,
            lineIdx: lineIdx,
            action_params: line.action_params,
            action_state: {
                dismissed: true,
            },
        };
        emitter.emit(eventName, action_data);
    }
};

export function lineIsNoteWidget(line: ChatMessageLine) {
    return lineIsWidget(line) && !lineHasWidget(line, ACTION.INTERNAL_THINKING) && !lineHasWidget(line, ACTION.VALUES_INSIGHT);
}
